import { cn, parseIdentifier } from '~/utils'
import {
  Globe,
  User,
  PanelTop,
  SquareStack,
  Workflow,
  MessagesSquare,
  MessageSquareMore,
  Zap,
  SquareTerminal,
  FileText,
  TriangleAlert,
  CircleDot,
} from 'lucide-react'
import { FC, useMemo, type ComponentProps, type ReactNode } from 'react'
import { Color } from '../types'
import { IdentifierDropdownMenu } from '~/components/IdentifierDropdownMenu'
import { Button } from '@bpinternal/ui-kit-next'

type Props = {
  id: string
  className?: string
  prefix?: ReturnType<typeof parseIdentifier>
  color?: Color
  path?: string
  studioUrl?: string
  fallback?: ReactNode
  truncate?: boolean
} & Pick<ComponentProps<typeof IdentifierDropdownMenu>, 'logsStartDate'>

const colorClasses = {
  gray: {
    button: 'hover:bg-neutral-50',
    icon: 'text-neutral-500',
    text: 'text-neutral-500',
    decoration: 'decoration-neutral-300 hover:decoration-neutral-600',
  },
  indigo: {
    button: 'hover:bg-indigo-50',
    icon: 'text-indigo-500',
    text: 'text-indigo-500',
    decoration: 'decoration-indigo-300 hover:decoration-indigo-600',
  },
  blue: {
    button: 'hover:bg-primary-50',
    icon: 'text-primary-500',
    text: 'text-primary-500',
    decoration: 'decoration-primary-300 hover:decoration-primary-600',
  },
  green: {
    button: 'hover:bg-green-50',
    icon: 'text-green-500',
    text: 'text-green-500',
    decoration: 'decoration-green-300 hover:decoration-green-600',
  },
}

export const Identifier = ({
  className,
  id,
  prefix: providedPrefix,
  color = 'gray',
  path,
  fallback,
  studioUrl,
  logsStartDate,
  truncate,
  ...props
}: Props) => {
  const prefix = providedPrefix ?? parseIdentifier(id)

  const prefixIconMap: Partial<Record<ReturnType<typeof parseIdentifier>, FC<{ className?: string }>>> = useMemo(() => {
    return {
      conv: MessagesSquare,
      msg: MessageSquareMore,
      evt: Zap,
      user: User,
      url: Globe,
      wf: Workflow,
      nd: PanelTop,
      ins: SquareStack,
      issevt: CircleDot,
      iss: TriangleAlert,
      file: FileText,
    }
  }, [id])

  const Icon = prefixIconMap[prefix] ?? SquareTerminal

  if (prefix === 'unknown') {
    return <>{fallback ?? id}</>
  }

  const colorScheme = colorClasses[color as keyof typeof colorClasses] || colorClasses.gray

  return (
    <IdentifierDropdownMenu
      path={path}
      value={id}
      prefix={prefix}
      studioUrl={studioUrl}
      logsStartDate={logsStartDate}
      onClick={(e) => e.stopPropagation()}
    >
      <Button
        {...props}
        variant="ghost"
        className={cn(
          '-mx-1 h-auto max-w-full cursor-pointer gap-1 rounded-sm p-1 [&_svg]:!size-3.5',
          colorScheme.button,
          className
        )}
      >
        <Icon className={cn(colorScheme.icon)} />
        <span
          className={cn(
            'font-mono text-xs underline decoration-dashed',
            colorScheme.text,
            colorScheme.decoration,
            truncate ? 'truncate' : ''
          )}
        >
          {id}
        </span>
      </Button>
    </IdentifierDropdownMenu>
  )
}
